import { Injectable } from '@angular/core';
import { WompiPaymentOrderResponse } from '../models/payment.model';

declare global {
  interface Window {
    WidgetCheckout?: new (options: WompiCheckoutOptions) => WompiCheckoutInstance;
    trustedTypes?: WompiTrustedTypesPolicyFactory;
  }
}

interface WompiCheckoutOptions {
  currency: string;
  amountInCents: number;
  reference: string;
  publicKey: string;
  redirectUrl?: string;
  expirationTime?: string;
  signature: {
    integrity: string;
  };
}

interface WompiCheckoutInstance {
  open(callback?: (result: unknown) => void): void;
}

interface WompiTrustedTypesPolicy {
  createScriptURL(value: string): unknown;
}

interface WompiTrustedTypesPolicyFactory {
  createPolicy(name: string, rules: { createScriptURL: (value: string) => string }): WompiTrustedTypesPolicy;
}

const WOMPI_SCRIPT_ID = 'wompi-checkout-widget';
const WOMPI_SCRIPT_SRC = 'https://checkout.wompi.co/widget.js';
const WOMPI_TRUSTED_TYPES_POLICY = 'wompi-widget';

let wompiTrustedTypesPolicy: WompiTrustedTypesPolicy | null = null;

@Injectable({ providedIn: 'root' })
export class WompiWidgetLoaderService {
  private loadingPromise: Promise<void> | null = null;

  load(): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.reject(new Error('Browser environment is required to load Wompi.'));
    }
    if (window.WidgetCheckout) {
      return Promise.resolve();
    }
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(WOMPI_SCRIPT_ID) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Wompi widget failed to load.')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = WOMPI_SCRIPT_ID;
      script.src = this.createTrustedScriptUrl(WOMPI_SCRIPT_SRC) as unknown as string;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Wompi widget failed to load.'));
      document.body.appendChild(script);
    });

    return this.loadingPromise;
  }

  async openCheckout(order: WompiPaymentOrderResponse, redirectUrl?: string): Promise<unknown> {
    await this.load();
    if (!window.WidgetCheckout) {
      throw new Error('Wompi widget is not available.');
    }

    return new Promise<unknown>((resolve) => {
      const checkout = new window.WidgetCheckout!({
        currency: order.currency,
        amountInCents: order.amountInCents,
        reference: order.reference,
        publicKey: order.publicKey,
        redirectUrl: redirectUrl || order.redirectUrl || undefined,
        expirationTime: order.expirationTime || undefined,
        signature: {
          integrity: order.integritySignature
        }
      });
      checkout.open((result: unknown) => resolve(result));
    });
  }

  private createTrustedScriptUrl(src: string): unknown {
    const trustedTypes = window.trustedTypes;
    if (!trustedTypes) {
      return src;
    }

    wompiTrustedTypesPolicy ??= trustedTypes.createPolicy(WOMPI_TRUSTED_TYPES_POLICY, {
      createScriptURL: (value: string) => {
        if (value !== WOMPI_SCRIPT_SRC) {
          throw new TypeError('Untrusted Wompi widget URL.');
        }
        return value;
      }
    });

    return wompiTrustedTypesPolicy.createScriptURL(src);
  }
}
